import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
} from 'reactstrap';
import useAuth from 'hooks/useAuth';
import dayjs from 'dayjs';
import { dashboardService } from '../../../../services/dashboardService';
import { getOperationTimezone } from '../../../../utils/timezone';
import { apiErrorMessage } from '../../../../utils/apiErrorMessage';
import { MetricSkeleton, ErrorState, COLORS, KpiCard, SortableTH } from '../../../../components/dashboard/shared/DashboardShared';
import DateRangeFilter from '../../../../components/dateRangeFilter/DateRangeFilter';
import { Table as UiTable, THead, TBody, TR, TH, TD } from '../../../../components/ui/Table';
import { Badge } from '../../../../components/ui/Badge';
import { Avatar } from '../../../../components/ui/Avatar';

// Presets del DateRangeFilter canónico. Incluye 'thisWeek' (opt-in) para no
// perder el 'Esta semana' que tenía el bloque propio anterior.
const DP_PRESETS = [
  { key: 'today', quick: true },
  { key: 'thisWeek', quick: true },
  { key: '7d', quick: true },
  { key: '30d', quick: true },
  { key: 'thisMonth', quick: true },
];

const pctTone = (pct) => {
  if (pct >= 80) return 'success';
  if (pct >= 50) return 'warning';
  return 'error';
};

const initialsOf = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

const formatMinutesToHHMM = (minutes) => {
  if (minutes == null || isNaN(minutes)) return '--:--';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

const [SORT_KEY_DEFAULT, SORT_DIR_DEFAULT] = ['totalDelivered', 'desc'];

const DriverPerformanceTab = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const logisticCompanyId = useMemo(() => user?.logisticCompany?._id || null, [user]);
  const dashboardIanaTz = useMemo(() => getOperationTimezone(user), [user]);

  const [startDate,    setStartDate]    = useState(() => dayjs().format('YYYY-MM-DD'));
  const [endDate,      setEndDate]      = useState(() => dayjs().format('YYYY-MM-DD'));
  const [activePreset, setActivePreset] = useState('today'); // key del DateRangeFilter (== rango default Hoy)
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [hasLoaded,    setHasLoaded]    = useState(false);
  const [sortKey,      setSortKey]      = useState(SORT_KEY_DEFAULT);
  const [sortDir,      setSortDir]      = useState(SORT_DIR_DEFAULT);

  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!logisticCompanyId) return;
    setLoading(true);
    setFetchError(null);
    try {
      const res = await dashboardService.getDriverPerformance(
        logisticCompanyId,
        startDate,
        endDate,
        dashboardIanaTz,
      );
      setData(res.data);
      setHasLoaded(true);
    } catch (err) {
      // Object, not the string itself: apiErrorMessage() can return null and the
      // state doubles as the "there is an error" flag.
      setFetchError({ message: apiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, [logisticCompanyId, startDate, endDate, dashboardIanaTz]);

  const sortedDrivers = useMemo(() => {
    if (!data?.drivers) return [];
    return [...data.drivers].sort((a, b) => {
      const mul = sortDir === 'asc' ? 1 : -1;
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      if (typeof aVal === 'string') return mul * aVal.localeCompare(bVal);
      return mul * (aVal - bVal);
    });
  }, [data, sortKey, sortDir]);

  const totals = useMemo(() => data?.totals ?? {
    totalDelivered: 0,
    avgServiceLevel: 0,
    avgAttentionLevel: 0,
    avgTimeInRoute: 0,
    avgRouteStart: '--:--',
  }, [data]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  return (
    <div className="cv-tab">

      {/* Controles */}
      <Card className="chart-card mb-3">
        <CardBody className="py-3">
          <div className="cv-controls">
            {/* Rango de fechas (DateRangeFilter canónico) */}
            <div className="cv-controls__group">
              <span className="cv-controls__label">{t('Período')}</span>
              <DateRangeFilter
                value={activePreset}
                onChange={(s, e, p) => { setStartDate(s); setEndDate(e); setActivePreset(p); }}
                presets={DP_PRESETS}
              />
            </div>

            <div className="cv-controls__divider" />

            <div className="cv-controls__group cv-controls__group--action">
              <span className="cv-controls__label">&nbsp;</span>
              <button
                className="cv-consult-btn"
                onClick={fetchData}
                disabled={loading}
              >
                <i className={`fas ${loading ? 'fa-sync-alt spinning' : 'fa-search'}`} />
                {loading ? t('Cargando…') : t('Consultar')}
              </button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Estado inicial / error */}
      {!hasLoaded && !loading && !fetchError && (
        <div className="cv-empty-state">
          <i className="fas fa-truck" />
          <p>{t('Selecciona un rango de fechas y haz clic en')} <strong>{t('Consultar')}</strong> {t('para ver la performance de los repartidores.')}</p>
        </div>
      )}
      {fetchError && !loading && (
        <ErrorState message={fetchError.message} onRetry={fetchData} />
      )}

      {/* Contenido */}
      {(hasLoaded || loading) && (
        <>
          {/* KPI cards */}
          <Row className="mb-3 g-3">
            {[
              {
                title: t('Nivel de Servicio'),
                value: `${totals.avgServiceLevel}%`,
                subtitle: `${totals.totalDelivered} ${t('entregados en el período')}`,
                color: COLORS.success,
                icon: 'fas fa-medal',
              },
              {
                title: t('Nivel de Atención'),
                value: `${totals.avgAttentionLevel}%`,
                subtitle: t('Entregados / (Entregados + Fallidos)'),
                color: COLORS.primary,
                icon: 'fas fa-bullseye',
              },
              {
                title: t('Tiempo en Ruta Prom.'),
                value: formatMinutesToHHMM(totals.avgTimeInRoute),
                subtitle: t('Desde salida a ruta hasta entrega'),
                color: COLORS.info,
                icon: 'fas fa-route',
              },
              {
                title: t('Salida a Ruta Prom.'),
                value: totals.avgRouteStart || '--:--',
                subtitle: t('Hora promedio de inicio de ruta'),
                color: COLORS.warning,
                icon: 'fas fa-clock',
              },
            ].map(kpi => (
              <Col key={kpi.title} xs="12" sm="6" lg="3">
                <KpiCard {...kpi} loading={loading} />
              </Col>
            ))}
          </Row>

          {/* Tabla ranking */}
          {!loading && sortedDrivers.length > 0 && (
            <Card className="chart-card">
              <CardHeader className="chart-card__header d-flex align-items-center justify-content-between">
                <h4 className="chart-card__title mb-0">
                  {t('Ranking de Repartidores')}
                </h4>
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                  {sortedDrivers.length} {t('repartidor')}{sortedDrivers.length !== 1 ? 'es' : ''}
                </span>
              </CardHeader>
              <CardBody className="p-0 cv-tblwrap">
                <UiTable>
                  <THead>
                    <TR>
                      <TH className="w-10">#</TH>
                      <SortableTH col="driverName" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>
                        {t('Repartidor')}
                      </SortableTH>
                      <SortableTH col="totalDelivered" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right">
                        {t('Entregados')}
                      </SortableTH>
                      <SortableTH col="serviceLevelPct" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right">
                        {t('Niv. Servicio')}
                      </SortableTH>
                      <SortableTH col="attentionLevelPct" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right">
                        {t('Niv. Atención')}
                      </SortableTH>
                      <SortableTH col="failedDeliveries" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right">
                        {t('Fallidos')}
                      </SortableTH>
                      <SortableTH col="avgTimeInRouteMinutes" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right">
                        {t('Tiempo Ruta')}
                      </SortableTH>
                      <TH className="text-right">{t('Salida Ruta')}</TH>
                      <SortableTH col="avgLastDeliveryTime" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right">
                        {t('Últ. Entrega')}
                      </SortableTH>
                    </TR>
                  </THead>
                  <TBody>
                    {sortedDrivers.map((d, i) => (
                      <TR key={d.driverId || i}>
                        <TD className="align-middle text-fg-subtle">{i + 1}</TD>
                        <TD className="align-middle">
                          <span className="inline-flex items-center gap-2 max-w-[240px]">
                            <Avatar size="sm" tone="brand" initials={initialsOf(d.driverName)} alt={d.driverName} />
                            <span className="font-semibold truncate" title={d.driverName}>{d.driverName}</span>
                          </span>
                        </TD>
                        <TD className="align-middle text-right font-semibold">{d.totalDelivered.toLocaleString()}</TD>
                        <TD className="align-middle text-right whitespace-nowrap">
                          <Badge tone={pctTone(d.serviceLevelPct)} className="min-w-[52px] justify-center">
                            {d.serviceLevelPct}%
                          </Badge>
                          <span className="ms-1 text-[11px] text-fg-muted">
                            ({d.deliveredBeforeCutoff}/{d.totalDelivered})
                          </span>
                        </TD>
                        <TD className="align-middle text-right">
                          <Badge tone={pctTone(d.attentionLevelPct)} className="min-w-[52px] justify-center">
                            {d.attentionLevelPct}%
                          </Badge>
                        </TD>
                        <TD className={`align-middle text-right ${d.failedDeliveries > 0 ? 'text-status-error font-semibold' : 'text-fg-subtle'}`}>
                          {d.failedDeliveries}
                        </TD>
                        <TD className="align-middle text-right">
                          {formatMinutesToHHMM(d.avgTimeInRouteMinutes)}
                        </TD>
                        <TD className="align-middle text-right">
                          {d.avgRouteStartTime || '--:--'}
                        </TD>
                        <TD className="align-middle text-right">
                          {d.avgLastDeliveryTime || '--:--'}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </UiTable>
              </CardBody>
            </Card>
          )}

          {!loading && sortedDrivers.length === 0 && hasLoaded && (
            <div className="cv-empty-state">
              <i className="fas fa-inbox" />
              <p>{t('No hay datos de repartidores para el rango seleccionado.')}</p>
            </div>
          )}

          {loading && (
            <Card className="chart-card">
              <CardBody>
                <div className="skeleton-shimmer" style={{ height: 320, borderRadius: 8 }} />
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// memo: no recibe props; lo aísla de los re-renders del dashboard padre.
export default React.memo(DriverPerformanceTab);
